import { generateDrivingLicence } from '@acorn/core';
import { safelyConsoleError } from './safely-console-error';
import { isDate } from 'date-fns';

interface IProps {
	title: string;
	lastNames: string;
	dob: Date | string;
	licenceNumber?: string;
}

/**
 * Get the initial driving licence fields based on the provided details. In priority order, we -
 * 1. Return an existing licence if available.
 * 2. If the licence number is not provided, it will generate a new one.
 * 3. If the licence generation fails, we can return an empty licence and log the error in development builds only.
 *
 * @param title - The title code of the driver e.g.`004` - Mrs; `003` - Mr
 * @param lastNames - The last name(s) of the driver
 * @param dob - The date of birth of the driver
 * @param licenceNumber - _optional_ - The existing licence number (if available)
 *
 * @remarks **There is date calculation involved in generating the licence, so correct typing is especially important.**
 */
const getInitialDrivingLicenceNumber = ({ title, lastNames, dob, licenceNumber }: IProps) => {
	if (licenceNumber && licenceNumber !== '') {
		return {
			'licenceField-1': licenceNumber.substring(0, 5),
			'licenceField-2': licenceNumber.substring(5, 11),
			'licenceField-3': licenceNumber.substring(11, 14),
			'licenceField-4': licenceNumber.substring(14, 16),
		};
	}

	const tempLicence = {
		'licenceField-1': '',
		'licenceField-2': '',
		'licenceField-3': '',
		'licenceField-4': '',
	};

	// Edge case to handle invalid date strings passed to e.g. parseISO
	if (!isDate(dob) || isNaN(new Date(dob).getTime())) {
		console.log('Invalid Date', dob);
		safelyConsoleError('Error generating driving licence with the date provided', new Error('Invalid Date'));
		return tempLicence;
	}

	// Edge case to handle invalid date strings passed to e.g. parseISO
	try {
		const licenceString = generateDrivingLicence({
			lastName: lastNames,
			dob: dob,
			title: title,
			licenceNumber: licenceNumber ?? '',
		});

		if (licenceString) {
			tempLicence['licenceField-1'] = licenceString.substring(0, 5);
			tempLicence['licenceField-2'] = licenceString.substring(5, 11);
		}
	} catch (error) {
		// If the licence generation fails, we can return an empty licence and log the error in development builds only
		safelyConsoleError('Error generating driving licence:', error);
		return tempLicence;
	}

	return tempLicence;
};

export default getInitialDrivingLicenceNumber;
